import React from "react";
import { Box, Grid, Typography, useTheme } from "@mui/material";

import Header from "../../components/Header";
import DashboardCard from "../../components/DashboardCard";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import StatusChip from "../../components/StatusChip";

import { useTenant } from "../../context/TenantContext";
import useRoleCoverageDashboard from "../../hooks/useRoleCoverageDashboard";
import { ROLE_ALLOCATION_VIEW_MODES } from "../../hooks/useRoleAllocationBoard";
import { tokens } from "../../theme/tokens";
import { countRiskTone, gapStatusTone } from "../../status/StatusChipTones";

const RoleCoverageDashboard = () => {
  const { tenantId } = useTenant();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data, loading, error } = useRoleCoverageDashboard({
    tenantId,
    departmentId: null,
    viewMode: ROLE_ALLOCATION_VIEW_MODES.ALL,
  });

  if (!tenantId) {
    return (
      <Box>
        <Header title="DASHBOARD" subtitle="Select a tenant to view dashboard insights" />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Header title="DASHBOARD" subtitle="Loading role coverage insights..." />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Header title="DASHBOARD" subtitle="Failed to load role coverage dashboard" />
      </Box>
    );
  }

  const departmentColumns = [
    { field: "departmentName", headerName: "Department", flex: 1.4, minWidth: 180 },
    { field: "totalRoles", headerName: "Total Roles", flex: 0.8, minWidth: 120 },
    { field: "withPeople", headerName: "With People", flex: 0.8, minWidth: 120 },
    { field: "withSops", headerName: "With SOPs", flex: 0.8, minWidth: 120 },
    { field: "fullyCovered", headerName: "Fully Covered", flex: 0.9, minWidth: 130 },
    {
      field: "gaps",
      headerName: "Gaps",
      flex: 0.7,
      minWidth: 100,
      renderCell: ({ value }) => (
        <StatusChip label={value} tone={countRiskTone(value)} />
      ),
    },
  ];

  const gapColumns = [
    { field: "roleName", headerName: "Role", flex: 1.2, minWidth: 170 },
    { field: "departmentName", headerName: "Department", flex: 1, minWidth: 150 },
    { field: "peopleCount", headerName: "People", flex: 0.6, minWidth: 90 },
    { field: "sopCount", headerName: "SOPs", flex: 0.6, minWidth: 90 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 180,
      renderCell: ({ value }) => <GapStatusChip status={value} />,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="OPERATIONAL COVERAGE"
          subtitle="View of role, people, and SOP coverage to answer Have we actually documented the business?"
        />
      </Box>

      <Grid container spacing={2}>
        <DashboardCard cardTitle="Total Roles" xs={12} sm={6} md={3} height="140px">
          <StatValue value={data.summary.totalRoles} />
        </DashboardCard>

        <DashboardCard cardTitle="Roles Without People" xs={12} sm={6} md={3} height="140px">
          <StatValue value={data.summary.rolesWithoutPeople} />
        </DashboardCard>

        <DashboardCard cardTitle="Roles Without SOPs" xs={12} sm={6} md={3} height="140px">
          <StatValue value={data.summary.rolesWithoutSops} />
        </DashboardCard>

        <DashboardCard cardTitle="Fully Covered Roles" xs={12} sm={6} md={3} height="140px">
          <StatValue value={data.summary.fullyCoveredRoles} />
        </DashboardCard>

        <DashboardCard cardTitle="Department Coverage" xs={12} md={12} height="420px">
          <ThemedDataGrid
            rows={data.departmentRows}
            columns={departmentColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard cardTitle="Roles Missing People" xs={12} md={6} height="420px">
          <ThemedDataGrid
            rows={data.rolesWithoutPeople}
            columns={gapColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard cardTitle="Roles Missing SOPs" xs={12} md={6} height="420px">
          <ThemedDataGrid
            rows={data.rolesWithoutSops}
            columns={gapColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>

        <DashboardCard cardTitle="Roles Missing Both" xs={12} md={12} height="320px">
          <SimpleGapList items={data.rolesWithoutAnything} colors={colors} />
        </DashboardCard>
      </Grid>
    </Box>
  );
};

const StatValue = ({ value }) => (
  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
    <Typography variant="h2" fontWeight="bold">
      {value}
    </Typography>
  </Box>
);

const GapStatusChip = ({ status }) => (
  <StatusChip label={status} tone={gapStatusTone(status)} />
);

const SimpleGapList = ({ items, colors }) => {
  if (!items.length) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography variant="body2" color="text.secondary">
          No roles are missing both people and SOPs.
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={1} pt={1} maxHeight="100%" overflow="auto">
      {items.map((item) => (
        <Box
          key={item.id}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1.2}
          sx={{
            bgcolor: colors.surface.card,
            border: `1px solid ${colors.surface.border}`,
            borderRadius: 2,
          }}
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {item.roleName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.departmentName}
            </Typography>
          </Box>

          <StatusChip label={item.status} tone={gapStatusTone(item.status)} />
        </Box>
      ))}
    </Box>
  );
};

export default RoleCoverageDashboard;